import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { config, GRADES, SUBJECTS, HEAR_ABOUT } from './config';
import { trackRegistration } from './tracking';
import { BrandPanel } from './BrandPanel';

interface FormValues {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone: string;
    grade_year: string;
    subjects: string[];
    hear_about: string;
    consent: boolean;
}

type SubmitState =
    | { kind: 'idle' }
    | { kind: 'submitting' }
    | { kind: 'error'; message: string }
    | { kind: 'success'; action: 'created' | 'updated' };

/** Stable per-browser id so repeated captures update one abandoned row. */
function getSessionKey(): string {
    const KEY = 'aft_session_key';
    try {
        let k = localStorage.getItem(KEY);
        if (!k) {
            k = 'aft' + Math.random().toString(36).slice(2) + Date.now().toString(36);
            localStorage.setItem(KEY, k);
        }
        return k;
    } catch {
        return 'aft' + Date.now().toString(36);
    }
}

function hasMeaningfulData(v: Partial<FormValues>): boolean {
    return Boolean(v.email || v.first_name || v.last_name || v.phone);
}

function capturePayload(v: Partial<FormValues>, sessionKey: string) {
    return {
        session_key: sessionKey,
        level: config.level,
        email: v.email || '',
        first_name: v.first_name || '',
        last_name: v.last_name || '',
        phone: v.phone || '',
        grade_year: v.grade_year || '',
        subjects: v.subjects || [],
        hear_about: v.hear_about || '',
    };
}

export default function App() {
    const {
        register,
        handleSubmit,
        control,
        watch,
        getValues,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: {
            email: '',
            password: '',
            first_name: '',
            last_name: '',
            phone: '',
            grade_year: '',
            subjects: [],
            hear_about: '',
            consent: false,
        },
    });

    const [state, setState] = useState<SubmitState>({ kind: 'idle' });
    const sessionKey = useRef(getSessionKey()).current;

    // Progressive abandoned-signup capture: debounce writes as the visitor
    // types, so a partial fill is saved even if they never submit.
    useEffect(() => {
        let timer: number | undefined;
        const sub = watch((values) => {
            window.clearTimeout(timer);
            if (!hasMeaningfulData(values as Partial<FormValues>)) return;
            timer = window.setTimeout(() => {
                fetch(`${config.restUrl}alt-free-trial/v1/capture`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': config.nonce },
                    body: JSON.stringify(capturePayload(values as Partial<FormValues>, sessionKey)),
                    keepalive: true,
                }).catch(() => {});
            }, 1200);
        });
        return () => {
            sub.unsubscribe();
            window.clearTimeout(timer);
        };
    }, [watch, sessionKey]);

    // Final capture on tab close / navigate away.
    useEffect(() => {
        const handler = () => {
            const values = getValues();
            if (!hasMeaningfulData(values)) return;
            try {
                const blob = new Blob([JSON.stringify(capturePayload(values, sessionKey))], {
                    type: 'application/json',
                });
                navigator.sendBeacon(`${config.restUrl}alt-free-trial/v1/capture`, blob);
            } catch {
                // ignore
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [getValues, sessionKey]);

    const onSubmit = async (values: FormValues) => {
        setState({ kind: 'submitting' });
        try {
            const res = await fetch(`${config.restUrl}alt-free-trial/v1/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': config.nonce,
                },
                body: JSON.stringify({ ...values, session_key: sessionKey, level: config.level }),
            });
            const data = await res.json();

            if (!res.ok || !data?.success) {
                setState({
                    kind: 'error',
                    message: data?.message || 'Something went wrong. Please try again.',
                });
                return;
            }

            trackRegistration({
                email: values.email,
                firstName: values.first_name,
                lastName: values.last_name,
                subjects: values.subjects,
                grade: values.grade_year,
            });

            setState({ kind: 'success', action: data.action === 'updated' ? 'updated' : 'created' });

            // Auto-login happens server-side; send them to the redirect target.
            // Small delay lets the Meta Pixel beacon fire before navigation.
            const redirectUrl: string = data.redirectUrl || config.successRedirectUrl;
            if (redirectUrl) {
                window.setTimeout(() => {
                    window.location.href = redirectUrl;
                }, 1000);
            }
        } catch {
            setState({
                kind: 'error',
                message: 'Network error. Please check your connection and try again.',
            });
        }
    };

    if (state.kind === 'success') {
        return (
            <div className="aft-page">
                <BrandPanel />
                <main className="aft-card-wrap">
                    <div className="aft-card">
                        <div className="aft-titlebar">
                            <span className="aft-close" aria-hidden="true" />
                            <span className="aft-stripes" aria-hidden="true" />
                            <span className="aft-tb">registration_complete</span>
                            <span className="aft-stripes" aria-hidden="true" />
                        </div>
                        <div className="aft-body aft-success">
                            <span className="aft-success-check" aria-hidden="true">✓</span>
                            <h1 className="aft-h1">You&rsquo;re in.</h1>
                            <p className="aft-lede">
                                {state.action === 'created'
                                    ? 'Your free-trial account is ready. Check your email for your login details.'
                                    : 'We’ve updated your account and unlocked your free trial.'}
                            </p>
                            {config.successRedirectUrl ? (
                                <a className="aft-submit aft-submit-link" href={config.successRedirectUrl}>
                                    Go to my dashboard
                                </a>
                            ) : null}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    const submitting = state.kind === 'submitting';

    return (
        <div className="aft-page">
            <BrandPanel />
            <main className="aft-card-wrap">
                <form className="aft-card" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="aft-titlebar">
                        <span className="aft-close" aria-hidden="true" />
                        <span className="aft-stripes" aria-hidden="true" />
                        <span className="aft-tb">free_signup</span>
                        <span className="aft-stripes" aria-hidden="true" />
                    </div>

                    <div className="aft-body">
                        <div className="aft-head">
                            <h1 className="aft-h1">Sign up</h1>
                            <p className="aft-lede">Start your free trial &mdash; it&rsquo;s quick and easy.</p>
                        </div>

                        {state.kind === 'error' ? (
                            <div className="aft-alert" role="alert">
                                {state.message}
                            </div>
                        ) : null}

                        {/* Email + password */}
                        <div className="aft-row">
                        <div className="aft-field">
                            <label className="aft-label" htmlFor="aft-email">
                                Email address
                            </label>
                            <input
                                id="aft-email"
                                type="email"
                                className={`aft-input ${errors.email ? 'aft-input-err' : ''}`}
                                placeholder="you@example.com"
                                autoComplete="email"
                                {...register('email', {
                                    required: 'Email is required',
                                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                                })}
                            />
                            {errors.email && <span className="aft-err">{errors.email.message}</span>}
                        </div>

                        {/* Password */}
                        <div className="aft-field">
                            <label className="aft-label" htmlFor="aft-password">
                                Password
                            </label>
                            <input
                                id="aft-password"
                                type="password"
                                className={`aft-input ${errors.password ? 'aft-input-err' : ''}`}
                                placeholder="Choose a password"
                                autoComplete="new-password"
                                {...register('password', {
                                    required: 'Password is required',
                                    minLength: { value: 6, message: 'At least 6 characters' },
                                })}
                            />
                            {errors.password && <span className="aft-err">{errors.password.message}</span>}
                        </div>
                        </div>

                        {/* Name row */}
                        <div className="aft-row">
                            <div className="aft-field">
                                <label className="aft-label" htmlFor="aft-first">
                                    First name
                                </label>
                                <input
                                    id="aft-first"
                                    type="text"
                                    className={`aft-input ${errors.first_name ? 'aft-input-err' : ''}`}
                                    placeholder="First name"
                                    autoComplete="given-name"
                                    {...register('first_name', { required: 'Required' })}
                                />
                                {errors.first_name && <span className="aft-err">{errors.first_name.message}</span>}
                            </div>
                            <div className="aft-field">
                                <label className="aft-label" htmlFor="aft-last">
                                    Last name
                                </label>
                                <input
                                    id="aft-last"
                                    type="text"
                                    className={`aft-input ${errors.last_name ? 'aft-input-err' : ''}`}
                                    placeholder="Last name"
                                    autoComplete="family-name"
                                    {...register('last_name', { required: 'Required' })}
                                />
                                {errors.last_name && <span className="aft-err">{errors.last_name.message}</span>}
                            </div>
                        </div>

                        {/* Phone + grade */}
                        <div className="aft-row">
                        <div className="aft-field">
                            <label className="aft-label" htmlFor="aft-phone">
                                Phone / mobile
                            </label>
                            <Controller
                                name="phone"
                                control={control}
                                rules={{
                                    required: 'Phone is required',
                                    validate: (v) =>
                                        isValidPhoneNumber(v || '') || 'Enter a valid phone number for the selected country',
                                }}
                                render={({ field }) => (
                                    <PhoneInput
                                        defaultCountry="pk"
                                        value={field.value}
                                        onChange={(phone, meta) => {
                                            // Users habitually type the national trunk "0" (e.g. 0300…).
                                            // E.164 drops it, so strip a leading 0 sitting right after the
                                            // dial code — turns "+920300…" into the correct "+92300…".
                                            const dial = `+${meta.country.dialCode}`;
                                            let next = phone;
                                            if (next.startsWith(dial) && next[dial.length] === '0') {
                                                next = dial + next.slice(dial.length).replace(/^0+/, '');
                                            }
                                            field.onChange(next);
                                        }}
                                        forceDialCode
                                        inputProps={{ id: 'aft-phone', name: 'phone', autoComplete: 'tel' }}
                                        className={`aft-phone ${errors.phone ? 'aft-input-err' : ''}`}
                                    />
                                )}
                            />
                            {errors.phone && <span className="aft-err">{errors.phone.message}</span>}
                        </div>

                        {/* Grade year */}
                        <div className="aft-field">
                            <label className="aft-label" htmlFor="aft-grade">
                                Grade year
                            </label>
                            <select
                                id="aft-grade"
                                className={`aft-input aft-select ${errors.grade_year ? 'aft-input-err' : ''}`}
                                {...register('grade_year', { required: 'Select your grade year' })}
                            >
                                <option value="">Grade year</option>
                                {GRADES.map((g) => (
                                    <option key={g} value={g}>
                                        {g}
                                    </option>
                                ))}
                            </select>
                            {errors.grade_year && <span className="aft-err">{errors.grade_year.message}</span>}
                        </div>
                        </div>

                        {/* Subjects */}
                        <div className="aft-field">
                            <span className="aft-label">What subjects are you currently studying?</span>
                            <div className="aft-checks">
                                {SUBJECTS.map((s) => (
                                    <label key={s} className="aft-check">
                                        <input
                                            type="checkbox"
                                            value={s}
                                            {...register('subjects', {
                                                validate: (v) => (v && v.length > 0) || 'Pick at least one subject',
                                            })}
                                        />
                                        <span>{s}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.subjects && <span className="aft-err">{errors.subjects.message as string}</span>}
                        </div>

                        {/* Hear about */}
                        <div className="aft-field">
                            <label className="aft-label" htmlFor="aft-hear">
                                Where did you hear about us?
                            </label>
                            <select id="aft-hear" className="aft-input aft-select" {...register('hear_about')}>
                                <option value="">Where did you hear about us?</option>
                                {HEAR_ABOUT.map((h) => (
                                    <option key={h} value={h}>
                                        {h}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Consent */}
                        <div className="aft-field">
                            <label className="aft-check aft-consent">
                                <input
                                    type="checkbox"
                                    {...register('consent', { required: 'Please accept to continue' })}
                                />
                                <span>
                                    I have read and understand the{' '}
                                    <a href={config.privacyUrl} target="_blank" rel="noreferrer">
                                        Privacy Policy
                                    </a>{' '}
                                    and{' '}
                                    <a href={config.termsUrl} target="_blank" rel="noreferrer">
                                        Terms and Conditions
                                    </a>{' '}
                                    and agree to receive personalised commercial communication from Alt.
                                </span>
                            </label>
                            {errors.consent && <span className="aft-err">{errors.consent.message}</span>}
                        </div>

                        <button type="submit" className="aft-submit" disabled={submitting}>
                            {submitting ? 'Creating your account…' : 'Sign up'}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}
